export const PATH = {
  PUBLIC: '/auth',
  PRIVATE: '/',
  SADMIN: '/sadmin',
  NOT_FOUND: '404',
};

export const PUBLIC_PATH = {
  LOGIN: '/auth/login',
  // RETURN_LOGIN: '/auth/returnLogin',
  PIN_LOGIN: '/auth/pin-login',
  FORGOT_PASSWORD: '/auth/forgotPassword',
  RESET_PASSWORD: '/auth/resetPassword',
  RESET_PASSWORD_LINK: (h = ':h') => `/auth/resetPassword?h=${h}`,
  VERIFY_EMAIL: '/auth/verify-email',
  VERIFY_EMAIL_LINK: (token = ':token') => `/auth/verify-email?token=${token}`,
  ONBOARDING: '/auth/onboarding/:token',
  ONBOARDING_LINK: (token = ':token') => `/auth/onboarding/${token}`,
  REGISTRY: '/auth/registry',
  RESELLER_IMPERSONATION_HANDOFF: '/reseller-impersonation-handoff',
  // Public system-status page. Reads the independent Cloudflare
  // health.json snapshot (NOT NeoAPI), so it stays consistent with
  // status.neogeniux.com and adds no NeoAPI dependency. Top-level
  // route (not under /auth) so it resolves at crm.neogeniux.com/health.
  HEALTH: '/health',
};

export const LEGAL_PATH = {
  TERMS: '/terminos-y-condiciones',
  PRIVACY: '/politica-de-privacidad',
};

export const PRIVATE_PATH = {
  DASHBOARD: '/dashboard',
  CONTRACT_SIGNING: '/contract-signing',
  POS: '/pos',
  INVOICES: `/invoices`,
  INVOICES_EDIT: (id = ':id') => `/invoices/${id}`,
  QUOTES: `/quotes`,
  CONDUCES: `/conduces`,
  SALES_ORDERS: '/sales-orders',
  SALES_ORDER_DETAIL: (id = ':id') => `/sales-orders/${id}`,
  RENTALS: '/rentals',
  RENTAL_PEDIDOS: '/rentals/pedidos',
  // Detail URL kept for list drill-in navigation. Post-save flow
  // stays on /new (RentalDetailPage renders in-place detail view
  // once `savedRental` is set on local state).
  RENTAL_PEDIDO_DETAIL: (id = ':id') => `/rentals/pedidos/${id}`,
  RENTAL_CALENDAR: '/rentals/calendar',
  RENTAL_RECOGIDO: '/rentals/recogido',
  APPROVALS: '/configuration/approvals',
  APPROVAL_DETAIL: (id = ':id') => `/configuration/approvals/${id}`,
  PAYMENTS: '/payments',
  // Phase 1 rename — the surface formerly known as "Purchase Orders"
  // is the Supplier Invoice entity (it carries AP debits, GL postings
  // and DGII NCFs). The freed `/purchase-orders` URL is reserved for
  // the new procurement Purchase Order entity (Phase 2). Existing
  // bookmarks redirect via the legacy route alias below.
  SUPPLIER_INVOICES: '/supplier-invoices',
  SUPPLIER_INVOICE: (id = ':id') => `/supplier-invoices/${id}`,
  // Back-compat aliases — keep the constants exported so the rest of
  // the codebase can be migrated piece by piece without breaking
  // anything that still references PURCHASE_ORDERS / PURCHASE_ORDER.
  // Phase 7 deletes these once every consumer has been updated.
  PURCHASE_ORDERS: '/supplier-invoices',
  PURCHASE_ORDER: (id = ':id') => `/supplier-invoices/${id}`,
  // New procurement Purchase Order entity (Phase 2) — operational
  // request document, no GL impact. Becomes a Supplier Invoice on
  // receive (Phase 4 wires up the auto-convert flow).
  PROCUREMENT_PURCHASE_ORDERS: '/purchase-orders',
  PROCUREMENT_PURCHASE_ORDER: (id = ':id') => `/purchase-orders/${id}`,
  // Supplier-tax-document detail lives under /reports/dgii — the
  // standalone list page was redundant with the DGII 606 report,
  // which now serves as the list surface and drills into this
  // detail on row click. No standalone list path remains.
  SUPPLIER_TAX_DOCUMENT: (id = ':id') =>
    `/reports/dgii/supplier-tax-documents/${id}`,
  INVENTORY_PRODUCTS: '/inventory/products',
  INVENTORY_PRODUCT: (id = ':id') => `/inventory/products/${id}`,
  INVENTORY_PRODUCT_INGREDIENTS: (productId = ':productId') =>
    `/inventory/products/${productId}/ingredients`,
  INVENTORY_CATEGORIES: '/inventory/categories',
  INVENTORY_WAREHOUSE: '/inventory/warehouses',
  // KDS moved to top-level — was '/inventory/kds'. Old path stays as
  // a redirect-source so any bookmark/external link survives.
  INVENTORY_KDS: '/kds',
  INVENTORY_KDS_LEGACY: '/inventory/kds',
  KDS: '/kds',
  KDS_REPORTS: '/kds/reports',
  INVENTORY_STOCK_MOVEMENT: '/inventory/stock-movement',
  FOLLOW_UP_REMINDERS: '/follow-up-reminders',
  // Configuration
  CONFIGURATION: '/configuration',
  CONFIGURATION_BUSINESS: '/configuration/business',
  CONFIGURATION_BUSINESS_SETTINGS: '/configuration/business-settings',
  CONFIGURATION_SALES_VENDORS: '/configuration/sales-vendors',
  CONFIGURATION_COMMISSION_CAMPAIGNS: '/configuration/commission-campaigns',
  CONFIGURATION_TAX: '/configuration/tax',
  CONFIGURATION_DELIVERY: '/configuration/delivery',
  CONFIGURATION_PROMOTIONS: '/configuration/promotions',
  CONFIGURATION_POS: '/configuration/template',
  CONFIGURATION_BARCODE_LABEL: '/configuration/barcode-label',
  CONFIGURATION_NCF: '/configuration/ncf',
  CONFIGURATION_ECF_ONBOARDING: '/configuration/ecf-onboarding',
  CONFIGURATION_ECF_BATCH: '/configuration/ecf-batch',
  CONFIGURATION_ECF_BATCH_DETAIL: (status = ':status') =>
    `/configuration/ecf-batch/${status}`,
  CONFIGURATION_ECF_CERTIFICATES: '/configuration/ecf-certificates',
  CONFIGURATION_ROLE_PERMISSIONS: '/configuration/role-permissions',
  CONFIGURATION_USERS: '/configuration/users',
  CONFIGURATION_SHIFTS: '/configuration/shifts',
  CONFIGURATION_BRANCHES: '/configuration/branches',
  CONFIGURATION_SYNCING: '/configuration/syncing',
  // Standalone paths (not under configuration)
  OPEN_CLOSE_SHIFT: '/open-close-shifts',
  CUSTOMERS: '/customers',
  PROVIDERS: '/providers',
  // Restaurant
  RESTAURANT_FLOOR_PLAN: '/restaurant/floor-plan',
  RESTAURANT_SERVICE: '/restaurant/service',
  // Reports
  REPORTS_SALES: '/reports/sales',
  REPORTS_INVENTORY: '/reports/inventory',
  REPORTS_USER_ACTIVITY: '/reports/user-activity',
  REPORTS_FINANCIAL: '/reports/financial',
  REPORTS_FINANCIAL_DETAIL: (type = ':type') => `/reports/financial/${type}`,
  REPORTS_DAILY: '/reports/daily-summary',
  REPORTS_PAYMENT_TYPES: '/reports/payment-types',
  REPORTS_PAYMENT_TYPE_DETAIL: (method = ':method') =>
    `/reports/payment-types/${method}`,
  REPORTS_DGII: '/reports/dgii',
  REPORTS_SALES_COMMISSION: '/reports/sales-commission',
  REPORTS_PROPINA: '/reports/propina',
  // Finanzas (Comercial plan and up)
  FINANCE: '/finance',
  // Expenses promoted to a top-level menu entry; backend REST surface
  // (`/finance/expenses`) is unchanged — only the FE route + sidebar
  // link relocate.
  FINANCE_EXPENSES: '/expenses',
  FINANCE_AR: '/finance/accounts-receivable',
  FINANCE_AP: '/finance/accounts-payable',
  FINANCE_CALENDAR: '/finance/calendario',
  // Accounting (Contable plan and up)
  ACCOUNTING: '/accounting',
  ACCOUNTING_DAILY_JOURNAL: '/accounting/daily-journal',
  ACCOUNTING_JOURNAL: '/accounting/journal-entries',
  ACCOUNTING_GENERAL_LEDGER: '/accounting/general-ledger',
  ACCOUNTING_CANCELLED_JOURNAL: '/accounting/cancelled-journals',
  ACCOUNTING_BANK_RECONCILIATION: '/accounting/bank-reconciliation',
  ACCOUNTING_FIXED_ASSETS: '/accounting/fixed-assets',
  ACCOUNTING_DEPRECIATIONS: '/accounting/depreciations',
  ACCOUNTING_BUDGETS: '/accounting/budgets',
  ACCOUNTING_BUDGET_DETAIL: '/accounting/budgets/:id',
  ACCOUNTING_REPORTS: '/accounting/reports',
  ACCOUNTING_AUDIT: '/accounting/audit',
  ACCOUNTING_DASHBOARD: '/accounting/dashboard',
  ACCOUNTING_SYSTEM_STATUS: '/accounting/system-status',
  ACCOUNTING_FINANCIAL_HEALTH: '/accounting/financial-health',
  // Configuration → Accounting
  CONFIGURATION_ACCOUNTING: '/configuration/accounting',
  CONFIGURATION_ACCOUNTING_NOTIFICATIONS:
    '/configuration/accounting/notifications',
  CONFIGURATION_ACCOUNTING_COA: '/configuration/accounting/chart-of-accounts',
  CONFIGURATION_ACCOUNTING_COST_CENTERS:
    '/configuration/accounting/cost-centers',
  CONFIGURATION_ACCOUNTING_CONCEPTS: '/configuration/accounting/concepts',
  CONFIGURATION_ACCOUNTING_PAYMENT_TYPES:
    '/configuration/accounting/payment-types',
  CONFIGURATION_ACCOUNTING_FISCAL_YEARS:
    '/configuration/accounting/fiscal-years',
  CONFIGURATION_ACCOUNTING_PERIODS: '/configuration/accounting/periods',
  CONFIGURATION_ACCOUNTING_PERIOD_CLOSE:
    '/configuration/accounting/period-close',
  CONFIGURATION_ACCOUNTING_BANK_ACCOUNTS:
    '/configuration/accounting/bank-accounts',
  CONFIGURATION_ACCOUNTING_DEFAULTS: '/configuration/accounting/defaults',
  CONFIGURATION_ACCOUNTING_TAX_MAP: '/configuration/accounting/tax-account-map',
  CONFIGURATION_ACCOUNTING_PURCHASE_EXPENSE:
    '/configuration/accounting/purchase-expense-accounts',
  CONFIGURATION_ACCOUNTING_POSTING_RULES:
    '/configuration/accounting/posting-rules',
  CONFIGURATION_ACCOUNTING_SCOPE: '/configuration/accounting/scope',
  CONFIGURATION_ACCOUNTING_BACKFILL: '/configuration/accounting/backfill',
  CONFIGURATION_ACCOUNTING_FA_CATEGORIES:
    '/configuration/accounting/fixed-asset-categories',
  CONFIGURATION_ACCOUNTING_EXPENSE_CATEGORIES:
    '/configuration/accounting/expense-categories',
  CONFIGURATION_ACCOUNTING_ADJUSTMENT_REASONS:
    '/configuration/accounting/adjustment-reasons',
  // User
  PROFILE: '/profile',
  ACCOUNT: '/account',
  // ROLES: '/users/roles',
  // PERMISSIONS_SETS: '/users/permissionsets',
  // PERMISSIONS: '/users/permissions',
};

// Paths that should not be included in the auth guard verification
export const NOT_INCLUDED_PATH = [
  PRIVATE_PATH.APPROVALS,
  PRIVATE_PATH.CONFIGURATION_POS,
  PRIVATE_PATH.PROFILE,
  PRIVATE_PATH.ACCOUNT,
  PRIVATE_PATH.CONFIGURATION_BUSINESS,
  PRIVATE_PATH.CONFIGURATION_TAX,
  PRIVATE_PATH.CONFIGURATION_PROMOTIONS,
  PRIVATE_PATH.CONFIGURATION_NCF,
  PRIVATE_PATH.CONFIGURATION_ECF_ONBOARDING,
  PRIVATE_PATH.CONFIGURATION_ECF_BATCH,
  PRIVATE_PATH.CONFIGURATION_ECF_CERTIFICATES,
  PRIVATE_PATH.CONFIGURATION_ROLE_PERMISSIONS,
  PRIVATE_PATH.CONFIGURATION_BUSINESS_SETTINGS,
  PRIVATE_PATH.CONFIGURATION_USERS,
  PRIVATE_PATH.CONFIGURATION_SHIFTS,
  PRIVATE_PATH.OPEN_CLOSE_SHIFT,
];

export const SADMIN_PATH = {
  BUSINESSES: '/sadmin/businesses',
  NOTIFICATIONS: '/sadmin/notifications',
  ECF_CERTIFICATION: '/sadmin/ecf-certification',
  SETTINGS: '/sadmin/settings',
  CONTRACTS: '/sadmin/contracts',
  COUPONS: '/sadmin/coupons',
  SYSTEM_ANNOUNCEMENTS: '/sadmin/system-announcements',
  BUSINESS_FEATURES: '/sadmin/business-features',
  BUSINESS_COMPOSITION: '/sadmin/business-composition',
  SADMIN_BILLING: '/sadmin/billing',
  FINANCIAL_EVENTS: '/sadmin/financial-events',
  PRODUCT_CATALOG: '/sadmin/product-catalog',
  PRODUCT_MODULES: (id = ':productId') =>
    `/sadmin/product-catalog/${id}/modules`,
  MODULE_FEATURES: (productId = ':productId', moduleId = ':moduleId') =>
    `/sadmin/product-catalog/${productId}/modules/${moduleId}/features`,
  ADDON_CATALOG: '/sadmin/addon-catalog',
  PRICING_CATALOG: '/sadmin/pricing-catalog',
  BUNDLE_CATALOG: '/sadmin/bundle-catalog',
  BUNDLE_ITEMS: (bundleId = ':bundleId') =>
    `/sadmin/bundle-catalog/${bundleId}/items`,
  PLAN_TEMPLATE_CATALOG: '/sadmin/plan-template-catalog',
  PLAN_TEMPLATE_ITEMS: (planTemplateId = ':planTemplateId') =>
    `/sadmin/plan-template-catalog/${planTemplateId}/items`,
  USAGE: '/sadmin/usage',
  CRONS: '/sadmin/crons',
  RESELLERS: '/sadmin/resellers',
  RESELLER_DETAIL: (id = ':id') => `/sadmin/resellers/${id}`,
  RESELLER_PAYOUTS: '/sadmin/reseller-payouts',
  RESELLER_DEAD_LETTER: '/sadmin/reseller-commissions/dead-letter',
};
